import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Tag } from 'primereact/tag'
import { UrgencyLongestWaitDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import { formatDuration, LONG_SERVICE_MINUTES, readablePriority, shortDayLabel, splitLocalTime } from '../../ServicesStatistics/periodDetail'
import { parseIsoDate } from '../../ServicesStatistics/statisticsUtils'
import { SectionStatus } from '../useSectionData'
import UrgencyDaySection from './UrgencyDaySection'

/** Cuántas atenciones se ven primero y cuántas con «Ver más» (el máximo del backend). */
export const LONGEST_WAITS_FIRST = 5
export const LONGEST_WAITS_MORE = 20

interface UrgencyLongestWaitsProps {
  rows: UrgencyLongestWaitDTO[]
  status: SectionStatus
  /** Ya se pidieron las 20: no hay «Ver más». */
  expanded: boolean
  /** El día que se mira (`yyyy-MM-dd`): el día solo se escribe cuando difiere de él. */
  day: string
  /** Con varias guardias y ninguna elegida, cada fila dice su guardia; si no, no se repite. */
  showTemplate: boolean
  onMore: () => void
  onRetry: () => void
}

/**
 * «Atenciones con más espera»: las atenciones que más esperaron ese día, de mayor a menor, sin datos de los pacientes. Es
 * una lista de lectura (las filas no se tocan): a la izquierda cuándo llegó y cuándo lo atendieron, y debajo la guardia y el
 * código; a la derecha la espera en grande, la duración de la atención y, con texto, las señales que ayudan a entender qué
 * pasó («Llegó el día anterior», «Atención muy larga»). Si hay cinco, «Ver más» pide veinte. Mismo formato que en
 * Estadísticas de atención.
 * TODO(design-system): una lista de solo lectura de dos renglones en un drawer no está en Novarium; se arma con las filas de
 * la lista de avisos (separador, alto holgado) y los tags de estado.
 */
export default function UrgencyLongestWaits({ rows, status, expanded, day, showTemplate, onMore, onRetry }: UrgencyLongestWaitsProps) {
  const intl = useIntl()
  const dayLabel = (date: string) => shortDayLabel(intl, parseIsoDate(date)).toLowerCase()
  // Al pedir más, las cinco primeras se siguen viendo mientras llegan las demás
  const sectionStatus: SectionStatus = status === 'failed' ? 'failed' : rows.length > 0 ? 'ready' : status

  return (
    <UrgencyDaySection
      id="nv-urg-detail-longest-title"
      title={intl.formatMessage({ id: 'UrgencySummaryDayLongestTitle' })}
      status={sectionStatus}
      failedMessageId="UrgencySummaryDayLongestFailed"
      onRetry={onRetry}
    >
      {rows.length === 0 ? (
        <p className="nv-note">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {intl.formatMessage({ id: 'UrgencySummaryDayLongestEmpty' })}
        </p>
      ) : (
        <>
          <ul className="nv-urg-detail__longest">
            {rows.map((row, index) => {
              const arrival = splitLocalTime(row.arrivalTime)
              const served = splitLocalTime(row.servedTime)
              const otherDay = arrival.date !== served.date
              // El día se escribe cuando difiere del que se mira (siempre si la llegada y la atención son de días distintos)
              const when = (part: { date: string; time: string }) => (otherDay || part.date !== day
                ? intl.formatMessage({ id: 'UrgencySummaryDayLongestOnDayAt' }, { day: dayLabel(part.date), time: part.time })
                : intl.formatMessage({ id: 'UrgencySummaryDayLongestAt' }, { time: part.time }))
              const details = [
                showTemplate ? row.templateName : null,
                row.priorityName ? readablePriority(row.priorityName) : null,
              ].filter((item): item is string => item !== null)
              return (
                <li key={`${row.arrivalTime}-${row.templateId}-${index}`} className="nv-urg-detail__longest-row">
                  <span className="nv-urg-detail__longest-main">
                    <span className="nv-urg-detail__longest-when">
                      {intl.formatMessage({ id: 'UrgencySummaryDayLongestRow' }, { arrival: when(arrival), served: when(served) })}
                    </span>
                    {details.length > 0 && <span className="nv-urg-detail__meta">{details.join(' · ')}</span>}
                  </span>
                  <span className="nv-urg-detail__longest-wait">
                    <span className="nv-urg-detail__big">{formatDuration(row.waitMinutes)}</span>
                    {row.serviceMinutes !== null && (
                      <span className="nv-urg-detail__meta">
                        {intl.formatMessage({ id: 'UrgencySummaryDayLongestService' }, { value: formatDuration(row.serviceMinutes) })}
                      </span>
                    )}
                    {arrival.date < day && (
                      <Tag className="nv-tag-neutral" value={intl.formatMessage({ id: 'UrgencySummaryDayLongestPrevDay' })} />
                    )}
                    {row.serviceMinutes !== null && row.serviceMinutes >= LONG_SERVICE_MINUTES && (
                      <Tag className="nv-tag-warn" value={intl.formatMessage({ id: 'UrgencySummaryDayLongestVeryLong' })} />
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
          {(status === 'loading' || (!expanded && rows.length >= LONGEST_WAITS_FIRST)) && (
            <div className="nv-urg-detail__more">
              {status === 'loading' ? (
                <span className="nv-loading" role="status">
                  <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
                  <span>{intl.formatMessage({ id: 'UrgencySummaryLoading' })}</span>
                </span>
              ) : (
                <Button type="button" link label={intl.formatMessage({ id: 'UrgencySummaryDayLongestMore' })} onClick={onMore} />
              )}
            </div>
          )}
        </>
      )}
    </UrgencyDaySection>
  )
}
